import { useCallback, useEffect, useRef, useState } from 'react';
import { Badge, Banner, BlockStack, Button, InlineStack, Text } from '@shopify/polaris';
import axios from 'axios';
import agent from '../../app/api/agent';
import { SyncServiceMaintenanceStatus } from '../../app/models/Admin';
import { formatLocalDateTime } from './utils/dateTime';
import './admin-console.css';

type Props = { token: string; onUnauthorized: () => void };
const pollIntervalMs = 5000;
const staleAfterMs = 15000;

export default function SyncServiceMaintenancePanel({ token, onUnauthorized }: Props) {
  const [status, setStatus] = useState<SyncServiceMaintenanceStatus | null>(null);
  const [checkedAt, setCheckedAt] = useState<number | null>(null);
  const [now, setNow] = useState(Date.now);
  const [verified, setVerified] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(false);
  const requestVersion = useRef(0);
  const readInFlight = useRef<number | null>(null);
  const mutationInFlight = useRef<number | null>(null);

  const fail = useCallback((e: unknown) => {
    setVerified(false);
    setError('Maintenance status could not be verified. Do not restart based on the last known status; refresh and wait for confirmation.');
    if (axios.isAxiosError(e) && e.response?.status === 401) onUnauthorized();
  }, [onUnauthorized]);

  const load = useCallback(async () => {
    if (readInFlight.current !== null || mutationInFlight.current !== null) return;
    const version = ++requestVersion.current;
    const startedAt = Date.now();
    readInFlight.current = version;
    setLoading(true);
    try {
      const result = await agent.Admin.getSyncMaintenance(token);
      if (!mounted.current || version !== requestVersion.current) return;
      setStatus(result);
      setCheckedAt(startedAt);
      setNow(Date.now());
      setVerified(true);
      setError(null);
    } catch (e) {
      if (mounted.current && version === requestVersion.current) fail(e);
    } finally {
      if (readInFlight.current === version) readInFlight.current = null;
      if (mounted.current && version === requestVersion.current) setLoading(false);
    }
  }, [token, fail]);

  useEffect(() => {
    mounted.current = true;
    setVerified(false);
    setBusy(false);
    void load();
    const pollTimer = window.setInterval(() => void load(), pollIntervalMs);
    // Expire a previously-ready response even if the next request never returns.
    const freshnessTimer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => {
      mounted.current = false;
      requestVersion.current += 1;
      readInFlight.current = null;
      mutationInFlight.current = null;
      window.clearInterval(pollTimer);
      window.clearInterval(freshnessTimer);
    };
  }, [load]);

  const fresh = verified && checkedAt !== null && now - checkedAt < staleAfterMs;
  const ready = fresh && status?.state === 'PausedReady' && status.readyToRestart
    && status.pauseRequested && status.activeWorkCount === 0 && status.activeBulkCount === 0
    && status.blockers.length === 0;
  const state = !fresh || !status ? 'Unavailable'
    : status.state === 'PausedReady' && !ready ? 'Draining' : status.state;
  const label = busy ? 'Updating pause' : ready ? 'Paused — ready to restart'
    : state === 'Draining' ? 'Draining' : state === 'Running' ? 'Running' : 'Status unavailable';

  const togglePause = async () => {
    if (!fresh || !status || mutationInFlight.current !== null) return;
    const paused = !status.pauseRequested;
    const message = paused
      ? 'Pause new ShopifySyncService work and let current work finish? Pending work is retained. Wait for “Paused — ready to restart” before patching or restarting. The pause remains in effect after restart until you choose Resume syncs.'
      : 'Resume ShopifySyncService work? Pending work and normal scheduling can start again.';
    if (!window.confirm(message)) return;
    const version = ++requestVersion.current;
    mutationInFlight.current = version;
    readInFlight.current = null;
    const startedAt = Date.now();
    setBusy(true);
    setVerified(false);
    setError(null);
    try {
      const result = await agent.Admin.setSyncMaintenance(token, paused);
      if (!mounted.current || version !== requestVersion.current) return;
      setStatus(result);
      setCheckedAt(startedAt);
      setNow(Date.now());
      setVerified(true);
    } catch (e) {
      if (mounted.current && version === requestVersion.current) fail(e);
    } finally {
      if (mutationInFlight.current === version) mutationInFlight.current = null;
      if (mounted.current && version === requestVersion.current) {
        setBusy(false);
        setLoading(false);
      }
    }
  };

  return <section aria-label="Sync service maintenance" style={{ borderBottom: '1px solid var(--p-color-border)', paddingBottom: 'var(--p-space-400)' }}>
    <BlockStack gap="200">
      <div className="admin-console-toolbar">
        <InlineStack gap="200" blockAlign="center">
          <Text as="h2" variant="headingMd">Shopify sync service</Text>
          <Badge tone={ready ? 'success' : state === 'Running' ? 'info' : 'attention'}>{label}</Badge>
        </InlineStack>
        <InlineStack gap="200">
          <Button onClick={() => void load()} loading={loading} disabled={busy}>Refresh status</Button>
          <Button variant="primary" onClick={() => void togglePause()} loading={busy} disabled={!fresh || !status}>
            {status?.pauseRequested ? 'Resume syncs' : 'Pause & drain'}
          </Button>
        </InlineStack>
      </div>
      {error ? <Banner tone="warning">{error}</Banner> : null}
      {ready ? <Text as="p">Current work has finished. You can patch or restart ShopifySyncService. Syncs stay paused until you choose Resume syncs.</Text>
        : state === 'Draining' ? <Text as="p">New work is paused. Current work is finishing; wait for ready-to-restart confirmation.</Text>
        : state === 'Running' ? <Text as="p" tone="subdued">Pause new work for maintenance without interrupting current syncs.</Text>
        : <Text as="p" tone="caution">{!status && loading ? 'Checking service status…'
          : !fresh ? 'A current service status is required before confirming it is ready to restart.'
            : 'Worker readiness is not confirmed. Do not restart based on an empty sync log list.'}</Text>}
      {fresh && status ? <Text as="p" tone="subdued">
        {status.activeWorkCount} active work items · {status.activeBulkCount} active bulk operations · {status.queuedBulkCount} bulk jobs {status.pauseRequested ? 'held' : 'waiting'} (not submitted to Shopify)
      </Text> : null}
      <details>
        <summary style={{ cursor: 'pointer', width: 'fit-content' }}>Maintenance details</summary>
        <div style={{ paddingTop: 'var(--p-space-200)' }}><BlockStack gap="100">
          <Text as="p" tone="subdued">This controls ShopifySyncService, not the separate ATD service. Incoming webhook requests remain saved for later processing. The pause survives a service restart.</Text>
          <Text as="p" tone="subdued">Already-running refresh and garage migration jobs finish before the service is ready. Pending work is not deleted.</Text>
          {status ? <>
            <Text as="p">Pause requested: {status.pauseRequested ? 'Yes' : 'No'}{!fresh ? ' (last known)' : ''}</Text>
            <Text as="p" tone="subdued">Last change: {formatLocalDateTime(status.requestedAt || undefined)}{status.requestedBy ? ` by ${status.requestedBy}` : ''}. Worker last seen: {formatLocalDateTime(status.workerLastSeenAt || undefined)}.</Text>
            {status.activeWork.map((work, index) => <Text as="p" key={`${work.worker}-${index}`}>{work.worker}: {work.count}</Text>)}
            {status.blockers.map((blocker, index) => <Text as="p" tone="caution" key={index}>{blocker}</Text>)}
          </> : null}
          <Text as="p" tone="subdued">Status updates every 5 seconds. Readiness comes from the service, not the displayed log rows.</Text>
        </BlockStack></div>
      </details>
    </BlockStack>
  </section>;
}
